import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Calendar,
  Receipt,
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronRight,
  Leaf,
  DollarSign,
  Briefcase,
  Clock,
  UserCheck,
  Building2,
  Euro,
  UserPlus,
  FileWarning
} from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [charts, setCharts] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [hotProspects, setHotProspects] = useState([]);
  const [prospectsStats, setProspectsStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchHotProspects();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, kpiRes, chartsRes, alertsRes, absencesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/kpi`),
        axios.get(`${API_URL}/dashboard/charts`),
        axios.get(`${API_URL}/dashboard/alerts`),
        axios.get(`${API_URL}/conges`)
      ]);

      setStats(statsRes.data);
      setKpi(kpiRes.data);
      setCharts(chartsRes.data);
      setAlerts(alertsRes.data || []);
      setAbsences((absencesRes.data || []).slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotProspects = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/prospects`, {
          headers,
          params: { score_min: 8, limit: 5 },
        }),
        axios.get(`${API_URL}/dashboard/prospects/stats`, { headers }),
      ]);
      setHotProspects(listRes.data || []);
      setProspectsStats(statsRes.data);
    } catch (error) {
      // Silencieux : la collection peut etre vide au debut
      console.log('Hot prospects non disponibles');
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value || 0);
  };

  // Use chart data from API or fallback
  const chartData = charts?.monthly || [];
  const pieData = charts?.absences_by_type || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  // KPI data for detailed view
  const effectif = kpi?.effectif || {};
  const masseSalariale = kpi?.masse_salariale || {};
  const alertesKpi = kpi?.alertes || {};

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Bienvenue, {user?.prenom || 'Utilisateur'}
          </h1>
          <p className="text-slate-500 mt-1">Voici un aperçu de votre activité RH</p>
        </div>
        <Badge variant="outline" className="w-fit">
          <Building2 className="w-4 h-4 mr-1" />
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Badge>
      </div>

      {/* Prospects CRM Widget — super_admin only */}
      {user?.role === 'super_admin' && (
        <Card className="border-red-100 dark:border-red-900/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="text-red-500">🔥</span>
                  Prospects chauds (Lucas)
                  {prospectsStats?.hot_leads_count > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-red-300 ml-1">
                      {prospectsStats.hot_leads_count}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  Top 5 des leads avec score ≥ 8 — {prospectsStats?.total ?? 0} prospects au total
                  {prospectsStats?.new_this_week > 0 && (
                    <span className="ml-1">
                      · {prospectsStats.new_this_week} nouveaux cette semaine
                    </span>
                  )}
                </CardDescription>
              </div>
              <Link to="/prospects">
                <Button variant="outline" size="sm">
                  Voir tous <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {hotProspects.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-3">
                Aucun prospect chaud pour l'instant. Lucas ajoutera des contacts ici lors des prochaines prospections.
              </p>
            ) : (
              <div className="space-y-2">
                {hotProspects.map((p) => (
                  <Link
                    key={p.id}
                    to="/prospects"
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.company_name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {p.sector} · {p.ville || 'ville inconnue'} · {p.employee_count || '?'} salariés
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Badge className="bg-red-100 text-red-700 border-red-300">
                        {p.score}
                      </Badge>
                      <Badge variant="outline" className="hidden md:inline-flex">
                        {p.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/employees" className="block">
          <Card className="card-stat group cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-card-0">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Effectif total
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="card-stat-value">{effectif.total || stats?.active_employees || 0}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {effectif.embauches_recentes > 0 ? (
                      <>
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-emerald-500">+{effectif.embauches_recentes} récent(s)</span>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">employés actifs</span>
                    )}
                  </div>
                </div>
                <div className="bg-[#0F5156] p-3 rounded-xl group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/absences" className="block">
          <Card className="card-stat group cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-card-1">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Congés en attente
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="card-stat-value">{stats?.pending_conges || 0}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {(stats?.pending_conges || 0) > 0 ? (
                      <>
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-amber-500">À traiter</span>
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-emerald-500">Tout validé</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="bg-amber-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                  <Calendar className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/habilitations" className="block">
          <Card className="card-stat group cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-card-2">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Alertes habilitations
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="card-stat-value">
                      {(stats?.habilitations_alerte || 0) + (stats?.habilitations_expirees || 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {(stats?.habilitations_expirees || 0) > 0 ? (
                      <>
                        <TrendingDown className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-red-500">{stats.habilitations_expirees} expirée(s)</span>
                      </>
                    ) : (
                      <span className="text-xs text-amber-500">À renouveler</span>
                    )}
                  </div>
                </div>
                <div className="bg-red-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                  <FileWarning className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/payroll" className="block">
          <Card className="card-stat group cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-card-3">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Masse salariale
                  </p>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="card-stat-value text-xl">{formatMoney(masseSalariale.brut_mensuel)}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs text-slate-400">brut mensuel</span>
                  </div>
                </div>
                <div className="bg-emerald-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                  <Euro className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* KPI Details Cards */}
      {kpi && !kpi.is_global && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Répartition par contrat</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(effectif.par_type_contrat || {}).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span className="text-sm">{type}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
                {Object.keys(effectif.par_type_contrat || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-2">Aucun contrat</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Répartition par département</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {Object.entries(effectif.par_departement || {}).map(([dept, count]) => (
                  <div key={dept} className="flex justify-between items-center">
                    <span className="text-sm truncate">{dept}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
                {Object.keys(effectif.par_departement || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-2">Aucun département</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Indicateurs clés</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Salaire moyen</span>
                  <span className="font-semibold">{formatMoney(masseSalariale.salaire_moyen)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Masse chargée/an</span>
                  <span className="font-semibold">{formatMoney(masseSalariale.chargee_annuel)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Contrats fin proche</span>
                  <Badge variant={alertesKpi.contrats_fin_proche > 0 ? 'destructive' : 'secondary'}>
                    {alertesKpi.contrats_fin_proche || 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Évolution mensuelle</CardTitle>
            <CardDescription>Absences validées sur les 6 derniers mois</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorAbsences" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0F5156" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0F5156" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                      formatter={(value, name) => [value, name === 'absences' ? 'Absences' : 'Dépenses (€)']}
                    />
                    <Area
                      type="monotone"
                      dataKey="absences"
                      stroke="#0F5156"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorAbsences)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <p>Aucune donnée disponible</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Types d'absences</CardTitle>
            <CardDescription>Répartition globale</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <p>Aucune absence</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {pieData.slice(0, 6).map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-slate-600 dark:text-slate-400 truncate">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alertes
            </CardTitle>
            <Link to="/habilitations">
              <Button variant="ghost" size="sm" className="gap-1">
                Voir tout <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">
                  Aucune alerte - tout est en ordre !
                </p>
              ) : (
                alerts.slice(0, 5).map((alert, index) => (
                  <Link
                    key={index}
                    to={alert.type === 'habilitation' ? '/habilitations' : '/absences'}
                    className="block"
                  >
                    <div
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                      data-testid={`alert-item-${index}`}
                    >
                      <div className={`w-2 h-2 mt-2 rounded-full ${
                        alert.priorite === 'critique' ? 'bg-red-500' : 'bg-amber-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {alert.message}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {alert.type === 'habilitation' ? 'Habilitation' : 'Congé'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </Link>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Absences */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Demandes récentes</CardTitle>
            <Link to="/absences">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-absences-btn">
                Voir tout <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {absences.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">
                  Aucune demande récente
                </p>
              ) : (
                absences.map((absence, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
                    data-testid={`absence-item-${index}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {absence.employee_name || 'Employé'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {absence.type_conge} • {formatDate(absence.date_debut)}
                      </p>
                    </div>
                    <Badge className={getStatusColor(absence.statut)}>
                      {getStatusLabel(absence.statut)}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/employees">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-action-employees">
                <UserPlus className="w-6 h-6 text-[#0F5156]" />
                <span className="text-sm">Nouvel employé</span>
              </Button>
            </Link>
            <Link to="/absences">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-action-absences">
                <Calendar className="w-6 h-6 text-amber-500" />
                <span className="text-sm">Valider absences</span>
              </Button>
            </Link>
            <Link to="/habilitations">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-action-habilitations">
                <FileText className="w-6 h-6 text-red-500" />
                <span className="text-sm">Habilitations</span>
              </Button>
            </Link>
            <Link to="/payroll">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-action-payroll">
                <Euro className="w-6 h-6 text-emerald-500" />
                <span className="text-sm">Préparation paie</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
